import { useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import {
  Dropdown,
  Option,
  useId,
} from "@fluentui/react-components";
import type { OptionOnSelectData, SelectionEvents } from "@fluentui/react-components";
import { ChevronDownRegular } from "@fluentui/react-icons";
import type { IPartnerInfo, IAssociatedPdm } from "@/types/partnerTypes";
import styles from "./NewRequestDialog.module.scss";

interface IManagerSelectionProps {
  partner: IPartnerInfo;
  pdms: IAssociatedPdm[];
  selectedPdmEmail: string;
  onPdmChange: (email: string) => void;
}

export function ManagerSelection({
  partner,
  pdms,
  selectedPdmEmail,
  onPdmChange,
}: IManagerSelectionProps): ReactNode {
  const dropdownId = useId("manager-select");

  const options = useMemo(
    () =>
      pdms.map((pdm) => ({
        value: pdm.email,
        text: pdm.displayName ?? pdm.email,
      })),
    [pdms],
  );

  const selectedText = useMemo(() => {
    const found = options.find((o) => o.value === selectedPdmEmail);
    return found?.text ?? "";
  }, [options, selectedPdmEmail]);

  const handleChange = useCallback(
    (_event: SelectionEvents, data: OptionOnSelectData): void => {
      if (data.optionValue) {
        onPdmChange(data.optionValue);
      }
    },
    [onPdmChange],
  );

  return (
    <div className={styles.selectionSection}>
      <p className={styles.selectionDescription}>
        {`It looks like you don't have permission to operate with partner ${partner.partnerName}. Please select a manager to approve your request.`}
      </p>
      <Dropdown
        id={dropdownId}
        className={styles.managerDropdown}
        placeholder="Select a manager"
        aria-label="Select a manager for approval"
        value={selectedText}
        selectedOptions={selectedPdmEmail ? [selectedPdmEmail] : []}
        onOptionSelect={handleChange}
        expandIcon={<ChevronDownRegular aria-hidden="true" />}
      >
        {options.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.text}
          </Option>
        ))}
      </Dropdown>
    </div>
  );
}
